// src/components/Funnels/SkillToSaleQuizFunnel/SuccessScreen.tsx
import React, { useState } from "react";
import { Zap } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAddWhatsappMutation } from "../../../app/api/roadmapApi";

interface LocationState {
  name?: string;
  email?: string;
}

const SuccessScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state || {}) as LocationState;
  const prefName = state.name ?? "";
  const prefEmail = state.email ?? "";

  const [name, setName] = useState<string>(prefName);
  const [email, setEmail] = useState<string>(prefEmail);
  const [whatsapp, setWhatsapp] = useState<string>("");
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Use RTK Query mutation
  const [addWhatsapp, { isLoading: isSending }] = useAddWhatsappMutation();

  const onRestart = () => {
    navigate("/skill-to-sales/quiz");
  };

  const submitWhatsapp = async () => {
    setErrorMsg(null);
    setSuccessMsg(null);

    if (!name || !email) {
      setErrorMsg("Please provide name and email before we send your strategy.");
      return;
    }

    const digits = whatsapp.replace(/\D/g, "");
    if (digits.length < 9) {
      setErrorMsg(
        "Please enter a valid WhatsApp number (include country code if outside Nigeria)."
      );
      return;
    }

    try {
      const payload = {
        name,
        email,
        whatsapp: digits,
        source: { name: "skill-to-sales-quiz-result" },
      };

      const res = await addWhatsapp(payload).unwrap(); // throws on error

      if (!res || !res.ok) {
        throw new Error(res?.message || "Failed to save WhatsApp number");
      }

      setSuccessMsg(
        "Done — your personalized strategy will be sent to WhatsApp shortly. Please check your messages (and spam) in case of delays."
      );
      setWhatsapp("");
    } catch (err: any) {
      setErrorMsg(err?.message || "Could not send your strategy. Try again shortly.");
    }
  };

  return (
    <div className="min-h-screen bg-brandNavy flex items-center justify-center p-6">
      <div className="max-w-xl w-full bg-white rounded-2xl shadow-2xl p-8">
        <div className="text-center mb-6">
          <div className="w-20 h-20 bg-gradient-to-r from-brandLightNavy to-brandNavy rounded-full flex items-center justify-center mx-auto mb-4">
            <Zap className="w-10 h-10 text-brandWhite" />
          </div>

          <h2 className="text-2xl font-bold text-brandNavy mb-2">
            Success! Your personalized revenue simulation has been delivered.
          </h2>

          <p className="text-sm text-mutedGray">
            We emailed your Million-Naira Roadmap to{" "}
            <strong>{email || "your inbox"}</strong>.
          </p>
        </div>

        <div className="bg-brandLightNavy text-brandWhite rounded-xl p-4 mb-6">
          <div className="font-semibold">Special Gift — Free Strategy</div>
          <div className="text-sm opacity-90 mt-1">
            <strong>My Personalized Digital Income Strategy Making Me Millions</strong>{" "}
            — a step-by-step supplement tailored to your quiz result. It'll be
            sent to your WhatsApp when you submit your number below.
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-mutedGray mb-1">
            Full name
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your full name"
            className="w-full p-3 rounded-lg border"
            style={{ borderColor: "#E9EDF3" }}
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-mutedGray mb-1">
            Email
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Your email address"
            className="w-full p-3 rounded-lg border"
            style={{ borderColor: "#E9EDF3" }}
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-mutedGray mb-1">
            WhatsApp number (where we'll send your strategy)
          </label>
          <input
            type="tel"
            value={whatsapp}
            onChange={(e) => setWhatsapp(e.target.value)}
            placeholder="+2348012345678"
            className="w-full p-3 rounded-lg border"
            style={{ borderColor: "#E9EDF3" }}
          />
        </div>

        {errorMsg && <div className="text-sm text-red-600 mb-3">{errorMsg}</div>}
        {successMsg && <div className="text-sm text-green-600 mb-3">{successMsg}</div>}

        <div className="grid grid-cols-1 gap-3">
          <button
            onClick={submitWhatsapp}
            disabled={isSending}
            className="w-full inline-flex items-center justify-center bg-brandRed text-brandWhite font-semibold px-4 py-3 rounded-lg shadow-sm hover:bg-brandRedHover transition"
          >
            {isSending ? "Sending to WhatsApp..." : "Send My Strategy to WhatsApp (Free)"}
          </button>

          <button
            onClick={onRestart}
            className="w-full inline-flex items-center justify-center bg-transparent border border-lightBorder text-brandNavy px-4 py-3 rounded-lg hover:bg-lightBorder transition"
          >
            Take Quiz Again
          </button>
        </div>

        <div className="mt-6 text-sm text-mutedGray">
          By submitting you agree to receive one message with your strategy and
          occasional follow-up about related training. Your number will not be
          shared.
        </div>
      </div>
    </div>
  );
};

export default SuccessScreen;
